import * as React from "react";
import { ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { CheckCircle, ErrorOutline } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useUploadDocumentMutation } from "../../services/cclTokenedAssessmentApi";
import { DocumentItem } from "../../services/types/assessmentAPITypes";

export enum eUploadFileState {
    Idle = 0,
    Uploading = 1,
    UploadSuccess = 2,
    UploadFailed = 3,
}

interface UploadFileListItemProps {
    file: File;
    startUpload: boolean;
    onUploadResult: (success: boolean, document?: DocumentItem) => void;
}

const UploadFileListItem: React.FC<UploadFileListItemProps> = (props) => {
    const [state, setState] = React.useState<eUploadFileState>(eUploadFileState.Idle);
    const [uploadFile, { data: uploadFileResponse, isSuccess: uploadFileIsSuccess, isError: uploadFileIsError }] =
        useUploadDocumentMutation();

    React.useEffect(() => {
        if (!uploadFileIsSuccess && !uploadFileIsError) return;
        if (uploadFileIsSuccess && uploadFileResponse && uploadFileResponse.success) {
            setState(eUploadFileState.UploadSuccess);
        } else {
            setState(eUploadFileState.UploadFailed);
        }
        props.onUploadResult(
            uploadFileIsSuccess && !!uploadFileResponse?.document,
            uploadFileResponse?.document,
        );
        // eslint-disable-next-line
    }, [uploadFileIsSuccess, uploadFileIsError]);

    // eslint-disable-next-line
    const startUpload = () => {
        setState(eUploadFileState.Uploading);
        const nameWithoutExtension = props.file.name.replace(/\.[^/.]+$/, "");
        const fd = new FormData();
        fd.append("cType", props.file.type);
        fd.append("fName", nameWithoutExtension);
        fd.append("file", new Blob([props.file], { type: props.file.type }), props.file.name);
        uploadFile(fd);
    };

    React.useEffect(() => {
        if (props.startUpload) {
            // in order for parent to start upload, we must be idle.
            // Retrying later is done only within the list item
            if (state === eUploadFileState.Idle) {
                startUpload();
            }
        }
        // eslint-disable-next-line
    }, [props.startUpload]);

    const getStateIcon = () => {
        switch (state) {
            case eUploadFileState.Idle:
                return <MoreHorizIcon color="primary" fontSize="small" />;
            case eUploadFileState.Uploading:
                return <CachedIcon color="primary" fontSize="small" />;
            case eUploadFileState.UploadSuccess:
                return <CheckCircle color="success" fontSize="small" />;
            case eUploadFileState.UploadFailed:
                return <ErrorOutline color="error" fontSize="small" />;
        }
    };

    return (
        <ListItem sx={{ borderTop: "1px solid lightgray" }}>
            <ListItemIcon>{getStateIcon()}</ListItemIcon>
            <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={props.file.name.replace(/\.[^/.]+$/, "")}
            />
        </ListItem>
    );
};

export default UploadFileListItem;
